import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { InitMynews } from "scmp-app/components/my-news/init-mynews";
import { Mynews } from "scmp-app/components/my-news/mynews";
import { MynewsLoading } from "scmp-app/components/my-news/mynews-loading";
import { trackingAtom } from "scmp-app/components/tracking";
import { useMyNews } from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { mynewsPageQuery } from "scmp-app/queries/__generated__/mynewsPageQuery.graphql";

import { PageContainer, PageWrapper } from "./styles";
import type { MyNewsAppConfig } from "./types";

const query = graphql`
  query mynewsPageQuery {
    appConfig(filter: { entityId: "scmp_pwa_my_news" }) {
      json
    }
  }
`;

const MynewsPage: RelayComponent<PageProps, mynewsPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const appConfig = data?.appConfig?.json as MyNewsAppConfig;

  const { hasEnoughFollowedItems, isDataLoaded } = useMyNews();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  const seo = {
    description:
      "Your source for credible news and authoritative insights from Hong Kong, China and the world.",
    openGraph: {
      description:
        "Your source for credible news and authoritative insights from Hong Kong, China and the world.",
      images: [
        {
          url: "https://assets-v2.i-scmp.com/production/_next/static/media/default-image.d1be8967.png",
        },
      ],
      title: "My News",
    },
    title: "My News",
  };

  return (
    <>
      <PageSeo {...seo} />
      <PageContainer>
        <PageWrapper>
          <ClientSideSuspense fallback={<MynewsLoading />}>
            {isDataLoaded ? (
              hasEnoughFollowedItems ? (
                <Mynews />
              ) : (
                <InitMynews topicPackages={appConfig?.topicPackages ?? []} />
              )
            ) : (
              <MynewsLoading />
            )}
          </ClientSideSuspense>
        </PageWrapper>
      </PageContainer>
      <AppFooter />
    </>
  );
};

MynewsPage.displayName = "MynewsPage";

const getPageProps = (_context: NextPageContext) => {
  const pageProps: PageProps = {
    appBarConfiguration: {
      adSlotVariants: {
        large: {
          height: 250,
          padding: 20,
        },

        small: {
          height: 100,
          padding: 10,
        },
      },
      hasDesktopAd: true,
      hasMobileAd: true,
      variant: "scmp/generic-light-with-banner-ad",
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 0,
        mobileUp: 0,
        tabletUp: 0,
      },
    },
  };

  return pageProps;
};

export default withRelay(MynewsPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
});
